import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { AppText } from "@toothfairy/shared-ui";
import React, { useState } from "react";
import { View, TouchableOpacity } from "react-native";
import { useTranslation } from "react-i18next";
import Charting from "../../../Components/Charting";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { adaptModeCodeToLabel } from "../../Settings/Components/QA/QaPreSelection";
import CurrentModelView from "./CurrentModelView";
import DashboardItem from "./DashboardItem";
import DashboardPlaceholder from "./DashboardPlaceholder";
import BirdsEyeView from "./BirdsEyeView";

const ModelView = ({
  intentsBreakDown,
  nersBreakDown,
  topicsBreakDown,
  documentsBreakdown,
  trainingAPIInProgress,
  agentsByMode,
  agentFunctionsByType,
  agents,
  agentsFunctions,
  execEnvs,
  authorisations,
  mrcDocuments,
  aiEntitiesConfig,
  jobs,
  triggers,
}) => {
  const { t } = useTranslation();
  const {
    isNLPModelTrained,
    isRCModelTrained,
    getActiveWorkspace,
  } = useWorkspaces();
  const { currentTheme } = AppTheme.useTheme();
  const [viewMode, setViewMode] = useState("standard");
  const doughnutChartOptions = {
    legend: {
      labels: {
        color: currentTheme?.theme?.font_color,
        font: {
          family: "normalFont",
          size: currentTheme?.theme?.extra_small_font_size,
        },
      },
    },
  };
  const commonOptions = {
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
        labels: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
    },
    scales: {
      x: {
        type: "logarithmic",
        display: true,
        position: "left",
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
          callback: function (value, index, values) {
            if (value >= 1)
              //needed to change the scientific notation results from using logarithmic scale
              return Number(value.toString()); //pass tick values as a string into Number function
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: currentTheme?.theme?.font_color,
          font: {
            family: "normalFont",
            size: currentTheme?.theme?.extra_small_font_size,
          },
        },
      },
    },
  };
  const hasAgentsByMode =
    agentsByMode && Object.values(agentsByMode).some((v) => v > 0);

  const hasAgentFunctionsByType =
    agentFunctionsByType &&
    Object.values(agentFunctionsByType).some((v) => v > 0);

  const functionTypeLabels = {
    api: t("dashboard.functionTypes.api"),
    mcp: t("dashboard.functionTypes.mcp"),
    callback: t("dashboard.functionTypes.callback"),
    database: t("dashboard.functionTypes.database"),
    html: t("dashboard.functionTypes.html"),
    chat: t("dashboard.functionTypes.chat"),
    handoff: t("dashboard.functionTypes.handoff"),
    code: t("dashboard.functionTypes.code"),
  };

  return (
    <View style={{ paddingBottom: 40 }}>
      <CurrentModelView />

      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-end",
          marginBottom: 15,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            borderRadius: 8,
            overflow: "hidden",
            borderWidth: 1,
            borderColor: currentTheme?.theme?.primary || "#6366f1",
          }}
        >
          <TouchableOpacity
            onPress={() => setViewMode("standard")}
            style={{
              backgroundColor:
                viewMode === "standard"
                  ? currentTheme?.theme?.primary || "#6366f1"
                  : "transparent",
              paddingHorizontal: 14,
              paddingVertical: 6,
            }}
          >
            <AppText
              style={{
                color:
                  viewMode === "standard"
                    ? currentTheme?.theme?.white || "#fff"
                    : currentTheme?.theme?.primary || "#6366f1",
                fontSize: currentTheme?.theme?.semi_small_font_size,
              }}
              fontWeight="600"
            >
              {t("dashboard.modelView.standardView")}
            </AppText>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setViewMode("birdsEye")}
            style={{
              backgroundColor:
                viewMode === "birdsEye"
                  ? currentTheme?.theme?.primary || "#6366f1"
                  : "transparent",
              paddingHorizontal: 14,
              paddingVertical: 6,
            }}
          >
            <AppText
              style={{
                color:
                  viewMode === "birdsEye"
                    ? currentTheme?.theme?.white || "#fff"
                    : currentTheme?.theme?.primary || "#6366f1",
                fontSize: currentTheme?.theme?.semi_small_font_size,
              }}
              fontWeight="600"
            >
              {t("dashboard.modelView.birdsEyeView")}
            </AppText>
          </TouchableOpacity>
        </View>
      </View>

      {viewMode === "birdsEye" ? (
        <BirdsEyeView
          agents={agents}
          agentsFunctions={agentsFunctions}
          execEnvs={execEnvs}
          authorisations={authorisations}
          mrcDocuments={mrcDocuments}
          aiEntitiesConfig={aiEntitiesConfig}
          jobs={jobs}
          triggers={triggers}
        />
      ) : (
      <>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: 20,
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <DashboardItem
          title={t("dashboard.modelView.agentsAvailable")}
          isHalfWidth={true}
          additionalTitle={
            agentsByMode?.total ? ` (${t("dashboard.modelView.total", { count: agentsByMode.total })})` : ""
          }
        >
          {hasAgentsByMode ? (
            <Charting.AppDoughnut
              options={doughnutChartOptions}
              maxHeight={Constants.doughnutChartMaxHeight}
              dataset={{
                labels: Object.keys(agentsByMode)
                  .filter((k) => k !== "total" && agentsByMode[k] > 0)
                  .map((k) => adaptModeCodeToLabel(k)),
                datasets: [
                  {
                    label: t("dashboard.modelView.agents"),
                    data: Object.keys(agentsByMode)
                      .filter((k) => k !== "total" && agentsByMode[k] > 0)
                      .map((k) => agentsByMode[k]),
                    backgroundColor: [
                      currentTheme?.theme?.primary,
                      currentTheme?.theme?.orange,
                      currentTheme?.theme?.green,
                      currentTheme?.theme?.purple,
                      currentTheme?.theme?.red,
                      currentTheme?.theme?.yellow,
                      currentTheme?.theme?.blue,
                      currentTheme?.theme?.mediumGrey,
                      currentTheme?.theme?.pink,
                      currentTheme?.theme?.cyan,
                    ],
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.modelView.noAgentsYet")}
              subCaption={t("dashboard.modelView.createFirstAgent")}
            />
          )}
        </DashboardItem>

        <DashboardItem
          title={t("dashboard.modelView.toolsByType")}
          isHalfWidth={true}
          additionalTitle={
            agentFunctionsByType?.total
              ? ` (${t("dashboard.modelView.total", { count: agentFunctionsByType.total })})`
              : ""
          }
        >
          {hasAgentFunctionsByType ? (
            <Charting.AppDoughnut
              options={doughnutChartOptions}
              maxHeight={Constants.doughnutChartMaxHeight}
              dataset={{
                labels: Object.keys(agentFunctionsByType)
                  .filter((k) => k !== "total" && agentFunctionsByType[k] > 0)
                  .map((k) => functionTypeLabels[k] || k),
                datasets: [
                  {
                    label: t("dashboard.modelView.tools"),
                    data: Object.keys(agentFunctionsByType)
                      .filter(
                        (k) => k !== "total" && agentFunctionsByType[k] > 0
                      )
                      .map((k) => agentFunctionsByType[k]),
                    backgroundColor: [
                      currentTheme?.theme?.primary,
                      currentTheme?.theme?.orange,
                      currentTheme?.theme?.green,
                      currentTheme?.theme?.purple,
                      currentTheme?.theme?.red,
                      currentTheme?.theme?.yellow,
                      currentTheme?.theme?.blue,
                      currentTheme?.theme?.mediumGrey,
                    ],
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.modelView.noFunctionsYet")}
              subCaption={t("dashboard.modelView.createFirstFunction")}
            />
          )}
        </DashboardItem>
      </View>

      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <DashboardItem title={t("dashboard.modelView.topics")}>
          {topicsBreakDown?.length > 0 ? (
            <Charting.AppBarChart
              vertical={false}
              maxHeight={Constants.chartMaxHeight}
              options={commonOptions}
              dataset={{
                labels: topicsBreakDown?.map(({ name }) => name),
                datasets: [
                  {
                    label: t("dashboard.modelView.records"),
                    data: topicsBreakDown?.map(({ count }) => count),
                    borderColor: topicsBreakDown?.map(({ color }) => color),
                    borderRadius: 10,
                    backgroundColor: topicsBreakDown?.map(({ color }) => color),
                    maxBarThickness: 50,
                  },
                ],
              }}
            />
          ) : (
            <DashboardPlaceholder
              mainCaption={t("dashboard.modelView.nothingToShow")}
              subCaption={t("dashboard.modelView.createFirstTopic")}
            />
          )}
        </DashboardItem>
        {
          <DashboardItem
            isFetchInProgress={trainingAPIInProgress}
            title={t("dashboard.modelView.documentsByType")}
            isHalfWidth={true}
          >
            {documentsBreakdown.reduce((a, b) => a + b, 0) > 0 ? (
              <Charting.AppDoughnut
                options={doughnutChartOptions}
                maxHeight={Constants.doughnutChartMaxHeight}
                dataset={{
                  labels: [
                    t("dashboard.modelView.knowledgeHub"),
                    t("dashboard.modelView.knowledgeHubQA"),
                    t("dashboard.modelView.knowledgeHubPDF"),
                    t("dashboard.modelView.knowledgeHubFiles"),
                    t("dashboard.modelView.knowledgeHubURL"),
                    t("dashboard.modelView.conversationalTraining"),
                  ],
                  datasets: [
                    {
                      label: t("dashboard.modelView.documents"),
                      data: documentsBreakdown,
                      backgroundColor: [
                        currentTheme?.theme?.primary,
                        currentTheme?.theme?.orange,
                        currentTheme?.theme?.green,
                        currentTheme?.theme?.blue,
                        currentTheme?.theme?.purple,
                        currentTheme?.theme?.red,
                        currentTheme?.theme?.yellow,
                      ],
                    },
                  ],
                }}
              />
            ) : (
              <DashboardPlaceholder
                mainCaption={t("dashboard.modelView.nothingToShow")}
                subCaption={t("dashboard.modelView.createFirstDocument")}
              />
            )}
          </DashboardItem>
        }
      </View>
      {getActiveWorkspace()?.isSentimentReviewerEnabled && (
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <DashboardItem title={t("dashboard.modelView.sentiments")}>
            {isNLPModelTrained() && intentsBreakDown ? (
              <Charting.AppBarChart
                vertical={false}
                maxHeight={Constants.chartMaxHeight}
                options={commonOptions}
                dataset={{
                  labels: intentsBreakDown?.map(({ name }) => name),
                  datasets: [
                    {
                      label: t("dashboard.modelView.records"),
                      data: intentsBreakDown?.map(({ count }) => count),
                      borderColor: intentsBreakDown?.map(({ color }) => color),
                      borderRadius: 10,
                      backgroundColor: intentsBreakDown?.map(
                        ({ color }) => color
                      ),
                      maxBarThickness: 50,
                    },
                  ],
                }}
              />
            ) : (
              <DashboardPlaceholder
                mainCaption={t("dashboard.modelView.nothingToShow")}
                subCaption={t("dashboard.modelView.createFirstIntent")}
              />
            )}
          </DashboardItem>
          <DashboardItem title={t("dashboard.modelView.keywords")}>
            {isNLPModelTrained() && nersBreakDown ? (
              <Charting.AppBarChart
                vertical={false}
                maxHeight={Constants.chartMaxHeight}
                options={commonOptions}
                dataset={{
                  labels: nersBreakDown?.map(({ name }) => name),
                  datasets: [
                    {
                      label: t("dashboard.modelView.records"),
                      data: nersBreakDown?.map(({ count }) => count),
                      borderColor: nersBreakDown?.map(({ color }) => color),
                      borderRadius: 10,
                      backgroundColor: nersBreakDown?.map(({ color }) => color),
                      maxBarThickness: 50,
                    },
                  ],
                }}
              />
            ) : (
              <DashboardPlaceholder
                mainCaption={t("dashboard.modelView.nothingToShow")}
                subCaption={t("dashboard.modelView.createFirstNER")}
              />
            )}
          </DashboardItem>
        </View>
      )}
      </>
      )}
    </View>
  );
};

export default ModelView;
